import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { isPathAllowed, getHomeRoute } from '../lib/roleRoutes';

/**
 * <RouteGuard /> — declarative role-aware redirect for the entire <Routes>
 * tree. Replaces the imperative `window.location.replace` calls that
 * previously lived inside App.js's mount effect and the ad-hoc <AdminOnly>
 * wrapper, both of which caused subtle stale-state and infinite-redirect
 * bugs (see /app/frontend/src/lib/roleRoutes.js header for context).
 *
 * Usage: wrap the top-level <Routes> tree with <RouteGuard user={user}>...</RouteGuard>.
 *
 * Behavior:
 *   - If `user` is not yet loaded → render children (the <Routes> tree
 *     itself contains a /login fallback for unauthenticated paths).
 *   - If the current pathname is allowed for the user's role → render children.
 *   - Otherwise → declarative <Navigate> to the user's home route.
 */
const RouteGuard = ({ user, children }) => {
  const location = useLocation();
  if (!user) return children;
  // Always allow the root path (App.js handles its own redirect for it),
  // login, and not-found bouncebacks via the trailing catch-all.
  const path = location.pathname;
  if (path === '/' || path === '/login') return children;
  if (isPathAllowed(user, path)) return children;
  return <Navigate to={getHomeRoute(user)} replace />;
};

export default RouteGuard;
